import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { LocalizePipe } from "@webapp/localization/pipes/localize.pipe";
import { CadenceChangeParams, DEFAULT_CADENCE_DURATION_IN_DAYS } from "@webapp/reflections/models/reflections.models";
import { CadencePagerDatePipe } from "@webapp/reflections/pipes/cadence-pager-date.pipe";
import { getLastDayOfCadence } from "@webapp/reflections/utils";
import dayjs from "@webapp/shared/libs/dayjs";

@Component({
  selector: "cadence-pager",
  templateUrl: "./cadence-pager.component.html",
  styleUrls: ["./cadence-pager.component.less"],
  providers: [CadencePagerDatePipe, LocalizePipe],
})
export class CadencePagerComponent implements OnInit {
  @Input() public cadenceStartDate: string;
  @Input() public latestAvailableDate: string;
  @Input() public isComparing: boolean;

  @Output() public readonly cadenceChange = new EventEmitter<CadenceChangeParams>();

  public cadenceEndDate: string;
  public prevCadenceStartDate: string;
  public prevCadenceEndDate: string;

  private latestCadenceStartDate: string;

  constructor(private cadencePagerDatePipe: CadencePagerDatePipe, private localizePipe: LocalizePipe) {}

  public ngOnInit(): void {
    this.latestCadenceStartDate = this.latestAvailableDate || this.cadenceStartDate;
    this.setCadenceDates();
  }

  public goToPrev(): void {
    let newCadenceStartDate = dayjs(this.cadenceStartDate).utc().subtract(DEFAULT_CADENCE_DURATION_IN_DAYS, "day");
    const isValidaCadanceStart = dayjs(newCadenceStartDate.toISOString()).day() === 1;
    if (!isValidaCadanceStart) {
      newCadenceStartDate = newCadenceStartDate.startOf("week").add(1, "day");
    }
    this.goTo(newCadenceStartDate.toISOString());
  }

  public canGoToNext(): boolean {
    return !this.latestAvailableDate || dayjs(this.cadenceEndDate).utc().isBefore(dayjs(this.latestAvailableDate).utc());
  }

  public goToNext(): void {
    const cadenceEndDate = getLastDayOfCadence(this.cadenceStartDate, DEFAULT_CADENCE_DURATION_IN_DAYS);
    const newCadenceStartDate = dayjs(cadenceEndDate).add(2, "hour").toISOString();
    this.canGoToNext() && this.goTo(newCadenceStartDate);
  }

  public canGoToLatest(): boolean {
    return this.cadenceStartDate !== this.latestCadenceStartDate;
  }

  public goToLatest(): void {
    this.canGoToLatest() && this.goTo(this.latestCadenceStartDate);
  }

  public buildPreviousWeekAriaLabel(): string {
    const currentCadence = `${this.cadencePagerDatePipe.transform(this.cadenceStartDate)} - ${this.cadencePagerDatePipe.transform(this.cadenceEndDate)}`;

    if (this.isComparing) {
      const previousCadence = `${this.cadencePagerDatePipe.transform(this.prevCadenceStartDate)} - ${this.cadencePagerDatePipe.transform(this.prevCadenceEndDate)}`;
      return `${this.localizePipe.transform("view_previous_week")}, ${previousCadence} | ${currentCadence}`;
    }

    return `${this.localizePipe.transform("view_previous_week")}, ${currentCadence}`;
  }

  private goTo(newCadenceStartDate: string): void {
    if (newCadenceStartDate && newCadenceStartDate !== this.cadenceStartDate) {
      this.cadenceStartDate = newCadenceStartDate;
      this.setCadenceDates();
      this.cadenceChange.emit({ currCadenceStartDate: newCadenceStartDate, currCadenceEndDate: this.cadenceEndDate });
    }
  }

  private setCadenceDates(): void {
    this.cadenceEndDate = dayjs(this.cadenceStartDate)
      .utc()
      .add(DEFAULT_CADENCE_DURATION_IN_DAYS - 1, "day")
      .toISOString();

    this.prevCadenceStartDate = dayjs(this.cadenceStartDate).utc().subtract(DEFAULT_CADENCE_DURATION_IN_DAYS, "day").toISOString();

    this.prevCadenceEndDate = dayjs(this.cadenceStartDate).utc().subtract(1, "day").toISOString();
  }
}
